import { Select } from "antd";
import React, { SetStateAction, useMemo, useState } from 'react';
import { useSelector } from "../../../util/store/store";
import { selectVessel } from "../../../util/store/vessel/constants";
import {
    eventTypes,
    KEY_SPLIT_CHECKLIST,
    OPERATION_TYPE,
    TASK_TYPE,
    taskTypes,
    treatmentTypes,
    VESSEL_TYPE,
    vesselTypes
} from "../../../util/varibles/constants";
import { filterOption, renderOpts } from "../../../util/varibles/global";
import { IChecklist, IIdName } from "../../../util/varibles/interface";

const validVesselTypes = new Set(vesselTypes.map(item => `vessel_type${KEY_SPLIT_CHECKLIST}${item.id}`));
const validOperationTypes = new Set([
    ...[OPERATION_TYPE.HARVEST, OPERATION_TYPE.TRANSPORT, OPERATION_TYPE.SERVICE]
        .map(id => ['operation_type', id].join(KEY_SPLIT_CHECKLIST)),
    ...treatmentTypes.map(item => ['operation_type', OPERATION_TYPE.TREATMENT, TASK_TYPE.TREATMENT, item.id].join(KEY_SPLIT_CHECKLIST)),
    ['operation_type', OPERATION_TYPE.TREATMENT, TASK_TYPE.MOVE].join(KEY_SPLIT_CHECKLIST),
    ...eventTypes.map(item => ['operation_type', OPERATION_TYPE.EVENT, item.id].join(KEY_SPLIT_CHECKLIST))
]);

const filterRequiredMap = (value: IChecklist['required_map']): IChecklist['required_map'] => {
    if (!value) return value;

    return {
        ...value,
        vessel_names: (value.vessel_names || []).filter(item => item
            && typeof item.id === 'string' && item.id.trim().length > 0
            && typeof item.name === 'string' && item.name.trim().length > 0
            && vesselTypes.some(type => type.id === item.type)),
        vessel_types: (value.vessel_types || []).filter(item => validVesselTypes.has(item)),
        sub_types: (value.sub_types || []).filter(item => validOperationTypes.has(item))
    };
};

interface ISectionProps {
    data: IChecklist['required_map']

    onChange(value: SetStateAction<IChecklist['required_map']>): void
}

const VesselTypes: React.FC<ISectionProps> = ({ data, onChange }) => {
    const handleChange = (value: string[]) => {
        const vesselTypes: Set<any> = new Set(value);
        onChange(prev => {
            const { vessel_names = [] } = prev || {};
            return {
                vessel_names: vessel_names.filter(item => vesselTypes.size === 0 || vesselTypes.has(item.type)),
                vessel_types: value,
                sub_types: prev?.sub_types || [],
                steps: prev?.steps || []
            }
        })
    }

    return <div>
        <div className='text-comment font-[600]'>Vessel type</div>
        <Select
            className="select-mode-tag w-full"
            mode="multiple"
            placeholder="Vessel type"
            filterOption={filterOption}
            value={data?.vessel_types || []}
            onChange={handleChange}
            options={vesselTypes.map(item => {
                const { id, name } = item;
                return {
                    value: `vessel_type${KEY_SPLIT_CHECKLIST}${id}`,
                    label: name
                }
            })}
        />
    </div>
}

const VesselNames: React.FC<ISectionProps> = ({ data, onChange }) => {
    const { data: vessels, loading } = useSelector(selectVessel.vesselContracts);

    const opts = useMemo(() => {
        const vesselTypes: Set<any> = new Set(data?.vessel_types || []);
        return renderOpts(vessels.filter(item => vesselTypes.size === 0 || vesselTypes.has(`vessel_type${KEY_SPLIT_CHECKLIST}${item.type}`)))
    }, [vessels, data?.vessel_types]);

    const handleChange = (value: string[]) => {
        const ids = new Set(value);
        onChange(prev => ({
            vessel_names: vessels.reduce((rs: ({ type: VESSEL_TYPE } & IIdName)[], item) => {
                const { id = "", name, type } = item;
                if (ids.has(id))
                    rs.push({ id, name, type });

                return rs;
            }, []),
            vessel_types: prev?.vessel_types || [],
            sub_types: prev?.sub_types || [],
            steps: prev?.steps || []
        }))
    }

    return <div>
        <div className='text-comment font-[600]'>Vessel name</div>
        <Select
            loading={loading}
            className="select-mode-tag w-full"
            mode="multiple"
            placeholder="Vessel"
            filterOption={filterOption}
            value={data?.vessel_names.map(item => item.id) || []}
            options={opts}
            onChange={handleChange}
        />
    </div>
}

const OperationType: React.FC<ISectionProps> = ({ data, onChange }) => {
    const handleChange = (value: string[]) => {
        onChange(prev => ({
            vessel_names: prev?.vessel_names || [],
            vessel_types: prev?.vessel_types || [],
            sub_types: value,
            steps: prev?.steps || []
        }))
    }

    const opts = useMemo(() => {
        // const filter = new Set<string>((data?.vessel_names || []).reduce((rs: string[], item) => {
        //     const vessel = vessels.find(sub => sub.id === item.id);
        //     if (vessel) {
        //         const {operation_type, operation_sub_types} = vessel;
        //         return [
        //             ...rs,
        //             ...operation_type.map(item => [`operation_type`, item].join(KEY_SPLIT_CHECKLIST)),
        //             ...operation_sub_types.map(key => [`operation_type`, OPERATION_TYPE.TREATMENT, TASK_TYPE.TREATMENT, key].join(KEY_SPLIT_CHECKLIST))
        //         ]
        //     }
        //     return rs;
        // }, []));
        //
        // const sectionA: {
        //     label: string | React.ReactNode,
        //     title?: string,
        //     value?: string,
        //     options?: { value: string, label: string }[]
        // }[] = [{
        //     label: "Harvest",
        //     value: [`operation_type`, OPERATION_TYPE.HARVEST].join(KEY_SPLIT_CHECKLIST)
        // },
        //     {label: "Smolt transport", value: [`operation_type`, OPERATION_TYPE.TRANSPORT].join(KEY_SPLIT_CHECKLIST)},
        //     {label: "Service", value: [`operation_type`, OPERATION_TYPE.SERVICE].join(KEY_SPLIT_CHECKLIST)},
        // ].filter(item => filter.has(item.value));
        // const treatment = treatmentTypes.filter(item => filter.has([`operation_type`, OPERATION_TYPE.TREATMENT, TASK_TYPE.TREATMENT, item.id].join(KEY_SPLIT_CHECKLIST)))
        // if (treatment.length > 0)
        //     sectionA.push({
        //         label: <span>Treatment</span>,
        //         title: 'Treatment',
        //         options: [
        //             ...treatment.map(item => {
        //                 const {id, name} = item;
        //                 return {
        //                     value: [`operation_type`, OPERATION_TYPE.TREATMENT, TASK_TYPE.TREATMENT, id].join(KEY_SPLIT_CHECKLIST),
        //                     label: name
        //                 }
        //             }),
        //             {
        //                 value: [`operation_type`, OPERATION_TYPE.TREATMENT, TASK_TYPE.MOVE].join(KEY_SPLIT_CHECKLIST),
        //                 label: taskTypes[TASK_TYPE.MOVE]?.name
        //             }
        //         ]
        //     })

        return [
            {
                label: "Harvest",
                value: [`operation_type`, OPERATION_TYPE.HARVEST].join(KEY_SPLIT_CHECKLIST)
            },
            { label: "Smolt transport", value: [`operation_type`, OPERATION_TYPE.TRANSPORT].join(KEY_SPLIT_CHECKLIST) },
            { label: "Service", value: [`operation_type`, OPERATION_TYPE.SERVICE].join(KEY_SPLIT_CHECKLIST) },
            {
                label: <span>Treatment</span>,
                title: 'Treatment',
                options: [
                    ...treatmentTypes.map(item => {
                        const { id, name } = item;
                        return {
                            value: [`operation_type`, OPERATION_TYPE.TREATMENT, TASK_TYPE.TREATMENT, id].join(KEY_SPLIT_CHECKLIST),
                            label: name
                        }
                    }),
                    {
                        value: [`operation_type`, OPERATION_TYPE.TREATMENT, TASK_TYPE.MOVE].join(KEY_SPLIT_CHECKLIST),
                        label: taskTypes[TASK_TYPE.MOVE]?.name
                    }
                ]
            },
            {
                label: <span>Event</span>,
                title: 'Event',
                options: eventTypes.map(item => {
                    const { id, name } = item;
                    return {
                        value: [`operation_type`, OPERATION_TYPE.EVENT, id].join(KEY_SPLIT_CHECKLIST),
                        label: name
                    }
                }),
            },
        ]
    }, []);

    return <div>
        <div className='text-comment font-[600]'>Operation type</div>
        <Select
            className="select-mode-tag w-full"
            mode="multiple"
            placeholder="Operation type"
            filterOption={filterOption}
            value={data?.sub_types || []}
            onChange={handleChange}
            options={opts}
        />
    </div>
}

/* Step trigger is temporarily disabled.
const Steps: React.FC<ISectionProps> = ({ data, onChange }) => {
    const { data: allSteps, loading } = useSelector(selectChecklist.steps);

    const opts = useMemo(() => Object.keys(allSteps).reduce((rs: {
        [id: string]: { value: string, label: string }[]
    }, key) => {
        rs[key] = allSteps[key].map(item => {
            const value = item.split(KEY_SPLIT_CHECKLIST);
            const name = firstLetter(value[value.length - 1].replace('_state', '').replace(/_/g, ' '));

            return { value: item, label: name }
        });
        return rs;
    }, {}), [allSteps]);

    const valueOfGroup = useMemo(() => {
        return (data?.steps || []).reduce((rs: { [id: string]: string[] }, item) => {
            const value = item.split(KEY_SPLIT_CHECKLIST);
            value.splice(value.length - 1, 1);
            const key = value.join(KEY_SPLIT_CHECKLIST);
            rs[key] = [...rs[key] || [], item];
            return rs;
        }, {});
    }, [data?.steps]);

    const group = useMemo(() => (data?.sub_types || []).reduce((rs: { [id: string]: IViewStep['data'][] }, item) => {
        const [, ...args] = item.split(KEY_SPLIT_CHECKLIST);
        const opType = args[0];
        const taskType = args[1];
        const treatmentType = args[2];
        rs[opType] = [...rs[opType] || [], { opType, taskType, treatmentType, id: args.join(KEY_SPLIT_CHECKLIST) }]
        return rs
    }, {}), [data?.sub_types]);

    if (!data?.sub_types?.length) return null;

    const handleChange = (id: string, value: string[]) => {
        onChange(prev => {
            const { steps: old = [] } = prev || {};
            return {
                vessel_names: prev?.vessel_names || [],
                vessel_types: prev?.vessel_types || [],
                sub_types: prev?.sub_types || [],
                steps: [
                    ...old.filter(item => item.slice(0, item.lastIndexOf(KEY_SPLIT_CHECKLIST)) !== id),
                    ...value
                ]
            }
        })
    }

    const handleClear = () => {
        onChange(prev => {
            return {
                vessel_names: prev?.vessel_names || [],
                vessel_types: prev?.vessel_types || [],
                sub_types: prev?.sub_types || [],
                steps: []
            }
        })
    }

    return <div>
        <Divider className='my-3' />
        <div className='flex items-center justify-between gap-3 mb-1'>
            <div className='text-comment font-[600]'>Step trigger</div>
            <div className='link text-[14px]' onClick={handleClear}>Clear All</div>
        </div>
        <div className='pl-5 flex flex-col gap-1'>
            {Object.keys(group).map(key => group[key].map(item => {
                const { [item.opType]: Name } = Names;
                return <div className={styles.stepTrigger} key={item.id}>
                    <div data-role="step-name" title="Harvest">
                        {Name?.({ data: item })}:
                    </div>
                    <Select
                        loading={loading}
                        className="select-mode-tag w-full"
                        mode="multiple"
                        placeholder="Step"
                        onChange={value => handleChange(item.id, value)}
                        options={opts[item.id] || []}
                        value={valueOfGroup[item.id] || []}
                    />
                </div>
            }))}
        </div>
    </div>
}
*/

interface IProps {
    data: IChecklist | Partial<IChecklist>

    onSave(value: IChecklist['required_map']): void

    onClose?(): void
}

const Assignments: React.FC<IProps> = ({ data, onSave, onClose }) => {
    const [requireMap, setRequireMap] = useState<IChecklist['required_map']>(() => filterRequiredMap(data.required_map));

    const handleSave = () => {
        const { vessel_types = [], vessel_names = [], sub_types = [], steps = [] } = filterRequiredMap(requireMap) || {};
        onSave({ vessel_names, vessel_types, sub_types, steps });
        onClose?.();
    }

    return <div>
        <div className='flex flex-col gap-2'>
            <VesselTypes data={requireMap} onChange={setRequireMap} />
            <VesselNames data={requireMap} onChange={setRequireMap} />
            <OperationType data={requireMap} onChange={setRequireMap} />
            {/* <Steps data={requireMap} onChange={setRequireMap} /> */}
        </div>

        <div className='text-comment italic text-[14px] my-4 leading-[16px]'>
            The checklist applies to operations that match one or more values from each field - AND between fields, OR
            within a field.
        </div>

        <div className='bt-primary ml-[-24px] mb-[-24px] w-[calc(100%+48px)!important] h-[44px!important]'
            data-full={true} onClick={handleSave}>
            Save
        </div>
    </div>;
};

export default Assignments;
